import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  Tooltip,
  Typography,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import * as actionsRelatorioJob from 'store/relatorioJob/actions';
import * as selectorsRelatorioJob from 'store/relatorioJob/reducer';
import {
  RELATORIO_JOB_POLL_MS,
  getJobSubtitle,
  isRelatorioJobAvailable,
  isRelatorioJobInFlight,
} from 'helpers/relatorioJob';
import RelatorioJobStatusIcon from 'views/v2/Downloads/RelatorioJobStatusIcon';
import RelatorioJobDownloadButton from 'views/v2/Downloads/RelatorioJobDownloadButton';
import moment from 'moment';

const formatWhen = (value) => {
  if (!value) return '';
  const m = moment(value);
  return m.isValid() ? m.fromNow() : '';
};

const RelatorioDownloadsMenu = ({
  items,
  totalElements,
  inFlightCount,
  fetchRelatorioJobsPreview,
  downloadRelatorioJob,
  downloadingId,
  history,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const visible = totalElements > 0 || (items && items.length > 0);

  useEffect(() => {
    fetchRelatorioJobsPreview();
  }, [fetchRelatorioJobsPreview]);

  useEffect(() => {
    const shouldPoll = (items || []).some((job) => isRelatorioJobInFlight(job.status));
    if (!shouldPoll) return undefined;
    const timer = window.setInterval(() => {
      fetchRelatorioJobsPreview();
    }, RELATORIO_JOB_POLL_MS);
    return () => window.clearInterval(timer);
  }, [items, fetchRelatorioJobsPreview]);

  if (!visible) return null;

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <Tooltip title="Downloads">
        <IconButton
          aria-label="downloads de relatórios"
          color="inherit"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            color: (theme) => (
              theme.palette.mode === 'dark'
                ? theme.palette.common.white
                : theme.palette.text.primary
            ),
          }}
        >
          <Badge badgeContent={inFlightCount} color="error" max={99}>
            <FileDownloadOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 400,
            maxWidth: 'calc(100vw - 32px)',
            borderRadius: 2,
            p: 0,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2">Downloads</Typography>
          <Typography variant="caption" color="text.secondary">
            Jobs do usuário
          </Typography>
        </Box>
        {(items || []).map((job) => (
          <Box
            key={job.id}
            sx={{
              display: 'flex',
              gap: 1.5,
              px: 2,
              py: 1.25,
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              alignItems: 'flex-start',
            }}
          >
            <RelatorioJobStatusIcon status={job.status} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} noWrap>
                {job.titulo}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                {getJobSubtitle(job)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatWhen(job.dataCriacao)}
              </Typography>
            </Box>
            {isRelatorioJobAvailable(job.status) && (
              <RelatorioJobDownloadButton
                job={job}
                downloadingId={downloadingId}
                onDownload={downloadRelatorioJob}
              />
            )}
          </Box>
        ))}
        <Divider />
        <Box sx={{ py: 1, textAlign: 'center' }}>
          <Button
            size="small"
            onClick={() => {
              handleClose();
              history.push('/v2/downloads');
            }}
          >
            Ver todos
          </Button>
        </Box>
      </Menu>
    </>
  );
};

RelatorioDownloadsMenu.propTypes = {
  items: PropTypes.array,
  totalElements: PropTypes.number,
  inFlightCount: PropTypes.number,
  fetchRelatorioJobsPreview: PropTypes.func.isRequired,
  downloadRelatorioJob: PropTypes.func.isRequired,
  downloadingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  history: PropTypes.object.isRequired,
};

RelatorioDownloadsMenu.defaultProps = {
  items: [],
  totalElements: 0,
  inFlightCount: 0,
  downloadingId: null,
};

const mapStateToProps = (state) => ({
  items: selectorsRelatorioJob.getRelatorioJobItems(state),
  totalElements: selectorsRelatorioJob.getRelatorioJobTotal(state),
  inFlightCount: selectorsRelatorioJob.getRelatorioJobInFlightCount(state),
  downloadingId: selectorsRelatorioJob.getRelatorioJobDownloadingId(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...actionsRelatorioJob }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(RelatorioDownloadsMenu));
